import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, Lock, Loader2, ShieldCheck, KeyRound, Eye, EyeOff } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { toast } from "sonner";
import { PORTAL_MODE, partnerSubdomainUrl, cedentePath } from "../lib/portal";
import CompanyDocuments from "../components/CompanyDocuments";

export default function Login() {
  const { user, login, verify2FA } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("credentials"); // credentials | totp | pending
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // docs-cadastro EAP-2: token de escopo do pendente (403 + `scope_token`), só em memória.
  const [pending, setPending] = useState(null);

  useEffect(() => {
    if (user) {
      if (user.portal_type === "partner") {
        // Partner user → redirect to parceiros.zemocapital.com
        const url = partnerSubdomainUrl("/dashboard");
        if (url) {
          toast.info("Conta de parceiro — redirecionando ao portal correto");
          window.location.href = url;
        } else {
          navigate("/dashboard", { replace: true });
        }
      } else {
        // Cedente user → stay here
        navigate(cedentePath("/operacoes"), { replace: true });
      }
    }
  }, [user, navigate]);

  const onSubmitCredentials = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await login(email.trim().toLowerCase(), password);
      if (r.pending) {
        setPending({ scopeToken: r.scope_token, message: r.message });
        setStep("pending");
        return;
      }
      if (r.requires_2fa) {
        setTempToken(r.temp_token);
        setStep("totp");
      } else {
        if (r.user.portal_type === "partner") {
          const url = partnerSubdomainUrl("/dashboard");
          if (url) {
            toast.info("Conta de parceiro — redirecionando ao portal correto");
            setTimeout(() => { window.location.href = url; }, 800);
          } else {
            toast.success("Login realizado com sucesso");
            navigate("/dashboard", { replace: true });
          }
          return;
        }
        toast.success("Login realizado com sucesso");
        navigate(cedentePath("/operacoes"), { replace: true });
      }
    } catch (e) {
      toast.error(e.message || "Falha no login");
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitOTP = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setSubmitting(true);
    try {
      const u = await verify2FA(tempToken, code);
      if (u.pending) {
        // Owner C1009746: pendente com 2FA ⇒ o mesmo painel do pendente.
        setPending({ scopeToken: u.scope_token, message: u.message });
        setStep("pending");
        return;
      }
      if (u.portal_type === "partner") {
        const url = partnerSubdomainUrl("/dashboard");
        if (url) {
          toast.info("Conta de parceiro — redirecionando ao portal correto");
          setTimeout(() => { window.location.href = url; }, 800);
        } else {
          navigate("/dashboard", { replace: true });
        }
        return;
      }
      toast.success("Acesso autorizado");
      navigate(cedentePath("/operacoes"), { replace: true });
    } catch (e) {
      toast.error(e.message || "Código inválido");
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="login-page">
      {/* Left – form */}
      <div className="flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[420px]">
          <div className="flex items-center gap-2.5 mb-12">
            <div className="h-9 w-9 rounded-md bg-[#0055FF] text-white flex items-center justify-center font-bold tracking-tight">
              Z
            </div>
            <div>
              <div className="text-[15px] font-semibold tracking-tight leading-none">Portal Zemo</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mt-1">
                Acesso unificado
              </div>
            </div>
          </div>

          {step === "credentials" && (
            <>
              <h1 className="text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900 leading-[1.1]">
                Entre em sua conta
              </h1>
              <p className="mt-2 text-sm text-zinc-500">
                Após o login, você será direcionado automaticamente para sua área —
                Parceiro ou Cedente.
              </p>

              <form onSubmit={onSubmitCredentials} className="mt-8 space-y-4" data-testid="login-credentials-form">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[13px] font-medium">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@parceiro.com.br"
                    required
                    className="h-11"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="pwd" className="text-[13px] font-medium">Senha</Label>
                    <span className="text-[12px] text-zinc-400">
                      <Link to="/esqueci-senha" className="hover:text-[#0055FF] hover:underline" data-testid="login-forgot-link">
                        Esqueceu a senha?
                      </Link>
                    </span>
                  </div>
                  <div className="relative">
                    <Input
                      id="pwd"
                      type={showPwd ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="h-11 pr-10"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                      tabIndex={-1}
                      aria-label="Mostrar senha"
                    >
                      {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold zemo-shine"
                  data-testid="login-submit-button"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Acessar portal
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <Link to="/cadastro" className="text-sm text-[#0055FF] hover:underline font-medium" data-testid="login-register-link">
                  Não tem conta? Cadastre-se
                </Link>
              </div>
            </>
          )}

          {step === "totp" && (
            <>
              <div className="h-12 w-12 rounded-md bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center mb-6">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 leading-[1.1]">
                Verificação em 2 etapas
              </h1>
              <p className="mt-2 text-sm text-zinc-500">
                Digite o código de 6 dígitos do seu app autenticador.
              </p>

              <form onSubmit={onSubmitOTP} className="mt-8" data-testid="login-totp-form">
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={setCode}
                  data-testid="totp-input"
                  autoFocus
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>

                <Button
                  type="submit"
                  disabled={submitting || code.length !== 6}
                  className="w-full h-11 mt-6 bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold"
                  data-testid="totp-submit-button"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Confirmar e entrar</>}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setStep("credentials");
                    setCode("");
                  }}
                  className="w-full mt-3 text-[12px] text-zinc-500 hover:text-zinc-700"
                  data-testid="totp-back-button"
                >
                  ← Voltar para login
                </button>
              </form>
            </>
          )}

          {step === "pending" && pending && (
            <div className="space-y-4" data-testid="login-pending-docs">
              {pending.message && <p className="text-sm text-zinc-600">{pending.message}</p>}
              <CompanyDocuments mode="pendente" scopeToken={pending.scopeToken} canUpload />
              <button
                type="button"
                onClick={() => { setPending(null); setStep("credentials"); }}
                className="w-full mt-3 text-[12px] text-zinc-500 hover:text-zinc-700"
                data-testid="pending-back-button"
              >
                ← Voltar para login
              </button>
            </div>
          )}

          <div className="mt-12 flex items-center gap-2 text-[11px] text-zinc-400">
            <KeyRound className="h-3 w-3" />
            <span>JWT · TOTP · TLS 1.3 · LGPD</span>
          </div>
        </div>
      </div>

      {/* Right – brand panel */}
      <div className="hidden lg:block relative bg-[#0a0a0a] overflow-hidden">
        <div className="absolute inset-0 zemo-grid-bg opacity-60"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[#0055FF]/30"></div>
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-zinc-400">
            ZemoBank · API de Antecipação
          </div>
          <div>
            <div className="text-[64px] leading-[0.95] font-semibold tracking-tight">
              Antecipe.<br />
              <span className="text-[#5b8cff]">Libere crédito.</span><br />
              Cresça.
            </div>
            <p className="mt-6 max-w-md text-zinc-400 text-[15px] leading-relaxed">
              Crie operações em segundos, gerencie cedentes e acompanhe a produção do seu time
              em um único portal.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-8 pt-10 border-t border-white/10">
            <div>
              <div className="text-3xl font-semibold tracking-tight font-mono-num">+R$ 2,4Bi</div>
              <div className="text-[11px] text-zinc-500 mt-1 uppercase tracking-wider">antecipados</div>
            </div>
            <div>
              <div className="text-3xl font-semibold tracking-tight font-mono-num">12k+</div>
              <div className="text-[11px] text-zinc-500 mt-1 uppercase tracking-wider">cedentes ativos</div>
            </div>
            <div>
              <div className="text-3xl font-semibold tracking-tight font-mono-num">99.9%</div>
              <div className="text-[11px] text-zinc-500 mt-1 uppercase tracking-wider">SLA</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
